// EA Hosting (EA Connect) standalone billing.
//
// One Stripe product with a FLAT per-unit monthly price: every bot slot costs
// the same, with no bundled minimum and no tier break.
// Subscription quantity = slots. Like AI Connect (mcpStandaloneBilling.js),
// EA Hosting is bought on its OWN subscription so a discount code entered at
// checkout reaches CreateSubscription and discounts EA Hosting alone (a
// Stripe discount is subscription-scoped).
//
// The same resolver also carries slot CHANGES: when the buyer already owns an
// active EA Hosting subscription, the item routes through the owner path in
// resolveCheckoutUpdates and comes out as a quantity modify on that sub's
// line (subscription_item_id included) instead of a create.

import { EA_HOSTING_PRODUCT, EA_HOSTING_MIN_SLOTS, eaHostingMonthlyCents } from './config.js';
import { resolveCatalogPrice, computeLineAmountCents, computeUseTrial } from './priceCatalog.js';
import { resolveCheckoutUpdates } from './subscriptionPlan.js';
import { findActiveAddon } from './selectionFromSubscription.js';
import { normalizeStatus } from './subscriptionStatuses.js';
import { findLineItemForProduct } from './subscriptionLines.js';

/** Clamp a requested slot count to the plan minimum (one bot). */
export function clampEaHostingSlots(slots) {
  const n = Math.floor(Number(slots) || 0);
  return Math.max(EA_HOSTING_MIN_SLOTS, n);
}

/**
 * The buyer's active EA Hosting subscription, or null.
 * Shape: { subscription, subscriptionItemId, cycle, currentPeriodEnd, lineCount }.
 */
export function findActiveEaHosting(subscriptions, catalog = null) {
  return findActiveAddon(subscriptions, EA_HOSTING_PRODUCT, catalog);
}

/** @returns {boolean} */
export function hasActiveEaHosting(subscriptions, catalog = null) {
  return Boolean(findActiveEaHosting(subscriptions, catalog));
}

/**
 * Slots on the buyer's EA Hosting subscription while it is on its free trial;
 * 0 when there is none or it is not trialing. Growing past both this and
 * EA_HOSTING_TRIAL_MAX_SLOTS ends the trial and bills the new slot count now
 * (billing-manager ModifySubscription compares the same billing line), so the
 * slot modal warns before that charge.
 */
export function eaHostingTrialingSlots(subscriptions, catalog = null) {
  const owner = findActiveEaHosting(subscriptions, catalog);
  if (!owner || normalizeStatus(owner.subscription) !== 'trialing') return 0;
  const line = catalog ? findLineItemForProduct(owner.subscription, EA_HOSTING_PRODUCT, catalog) : null;
  return Math.max(EA_HOSTING_MIN_SLOTS, Number(line?.quantity) || 0);
}

/**
 * Desired-state item for the EA Hosting plan at `slots`.
 * Empty array when the catalog has no `ea_connect_<cycle>` price (the CTA
 * must stay disabled rather than quote $0).
 */
export function buildEaHostingCheckoutItems(catalog, cycle, slots, options = {}) {
  if (!catalog || !cycle) return [];
  const price = resolveCatalogPrice(catalog, EA_HOSTING_PRODUCT, cycle, options);
  if (!price?.id) return [];
  return [{ price_id: price.id, quantity: clampEaHostingSlots(slots) }];
}

/**
 * Checkout plan for EA Hosting at `slots`.
 *
 * New buyer → create-only plan (own subscription, coupon-scoped). Whether
 * that create trials is decided at execution from the price's
 * trial_period_days and the used-trial set (buildCheckoutPlan's trialContext),
 * not here.
 * Existing subscriber → quantity modify on the owned sub's EA line, pinned to
 * that sub's billing cycle so the resolver can never split it into a second
 * subscription. Returns null when the price is missing from the catalog.
 */
export function resolveEaHostingCheckout(catalog, cycle, slots, subscriptions = []) {
  const owner = findActiveEaHosting(subscriptions, catalog);
  const effectiveCycle = owner?.cycle || cycle;
  const items = buildEaHostingCheckoutItems(catalog, effectiveCycle, slots, { subscriptions });
  if (items.length === 0) return null;
  // Only the owned EA sub participates: the resolver must never read the rest
  // of the cart (this is a single-product flow, same as MCP standalone).
  const scope = owner ? [owner.subscription] : [];
  return resolveCheckoutUpdates(items, scope, catalog, { usedProductIds: [], skipTrial: true });
}

/**
 * Free-trial days a NEW EA Hosting buyer gets on up to
 * EA_HOSTING_TRIAL_MAX_SLOTS slots (more are paid from day one): the price's
 * trial_period_days (configured per price in billing, served by the prices
 * list), or 0 when the
 * price has none, the EA trial is already used (`usedProductIds` =
 * BillingOverview.trialUsedProductIds), or EA Hosting is already owned —
 * slot changes never trial. Same eligibility the checkout executes
 * (computeUseTrial), so the promise matches the charge.
 */
export function eaHostingTrialDays(catalog, cycle, subscriptions = [], usedProductIds = []) {
  if (!catalog || !cycle || hasActiveEaHosting(subscriptions, catalog)) return 0;
  const items = buildEaHostingCheckoutItems(catalog, cycle, EA_HOSTING_MIN_SLOTS, { subscriptions });
  if (items.length === 0 || !computeUseTrial(items, catalog, usedProductIds)) return 0;
  return Number(catalog.getById?.(items[0].price_id)?.trial_period_days) || 0;
}

/**
 * Live cycle price (cents) for `slots`, falling back to the offline contract
 * amounts when the catalog price is unavailable.
 */
export function eaHostingCycleCents(catalog, cycle, slots) {
  const n = clampEaHostingSlots(slots);
  if (catalog && cycle) {
    const price = resolveCatalogPrice(catalog, EA_HOSTING_PRODUCT, cycle);
    if (price) return computeLineAmountCents(price, n);
  }
  return eaHostingMonthlyCents(n);
}

/**
 * Live price per bot slot (cents). The price is flat per unit, so one slot's
 * cost IS the rate — every additional bot costs exactly the same, which is the
 * whole point of the plan and what the UI quotes.
 */
export function eaHostingSlotCents(catalog, cycle) {
  if (catalog && cycle) {
    const price = resolveCatalogPrice(catalog, EA_HOSTING_PRODUCT, cycle);
    if (price) return computeLineAmountCents(price, 1);
  }
  return eaHostingMonthlyCents(1);
}
